/**
 * Button Component
 * Adapted from web team's "next" project for Vortex
 *
 * Provides a consistent button system with multiple types, sizes, and states.
 * Uses shared CSS classes from button.css (nxm-button-*) for styling.
 */
import { mdiCircleOutline, mdiLoading } from "@mdi/js";
import React, { forwardRef, } from "react";
import { Icon } from "@/ui/components/icon/Icon";
import { joinClasses } from "@/ui/utils/joinClasses";
const getButtonClasses = ({ buttonType, disabled, filled, iconOnly, size, }) => {
    const classes = [
        "nxm-button",
        {
            "nxm-button-disabled": disabled,
            "nxm-button-icon-only": iconOnly,
            "nxm-button-xs": size === "xs",
            "nxm-button-sm": size === "sm",
        },
    ];
    switch (buttonType) {
        case "primary":
            classes.push("nxm-button-primary");
            break;
        case "secondary":
            if (filled) {
                classes.push(filled === "strong"
                    ? "nxm-button-secondary-filled-strong"
                    : "nxm-button-secondary-filled-weak");
            }
            else {
                classes.push("nxm-button-secondary");
            }
            break;
        case "tertiary":
            if (filled) {
                classes.push(filled === "strong"
                    ? "nxm-button-tertiary-filled-strong"
                    : "nxm-button-tertiary-filled-weak");
            }
            else {
                classes.push("nxm-button-tertiary");
            }
            break;
        case "success":
            classes.push("nxm-button-success");
            break;
        case "premium":
            classes.push("nxm-button-premium");
            break;
    }
    return classes;
};
const ButtonIcon = ({ icon, isLoading = false, path, }) => {
    if (isLoading) {
        return (React.createElement("span", { className: "nxm-button-icon relative animate-spin" },
            React.createElement(Icon, { className: "opacity-40", path: mdiCircleOutline, size: "none" }),
            React.createElement(Icon, { className: "absolute inset-0", path: mdiLoading, size: "none" })));
    }
    if (icon) {
        return (React.createElement("span", { className: "nxm-button-icon flex items-center justify-center" }, icon));
    }
    if (path) {
        return React.createElement(Icon, { className: "nxm-button-icon", path: path, size: "none" });
    }
    return null;
};
export const Button = forwardRef(({ "aria-disabled": ariaDisabled, buttonType = "primary", children, className, customContent, disabled, filled, isExternal, isLoading = false, leftIcon, leftIconPath, rightIcon, rightIconPath, size = "md", ...props }, ref) => (React.createElement("button", { "aria-disabled": ariaDisabled, className: joinClasses([
        ...getButtonClasses({
            buttonType,
            disabled: !!disabled || !!ariaDisabled || isLoading,
            filled,
            iconOnly: !customContent && !children,
            size,
        }),
        className || "",
    ]), disabled: disabled || isLoading, ref: ref, type: props.type ?? "button", ...props }, customContent ?? (React.createElement(React.Fragment, null,
    React.createElement(ButtonIcon, { icon: leftIcon, isLoading: isLoading, path: leftIconPath }),
    !!children && React.createElement("span", null, children),
    React.createElement(ButtonIcon, { icon: rightIcon, path: rightIconPath }))))));
